import moment from 'moment';
import styled from 'styled-components';
import React, { useRef } from 'react';
import { useMemo, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { Tooltip, Button, Modal, Tag, Table } from "antd";
import { ModalHeader, expandIcon } from "datastake-daf/dist/components";
import { Animated } from './tasks';
import { useModule } from '../../../../../../helpers/user';
import { ActionsColumnStyle } from "./recommendations/style";
import { getType } from '../../../../../../helpers/dataMapping/scl';
import TablePagination from '../../../../../../components/Table/pagination';
import ModalContent from "../../../../../../components/MeassureModalContent";
import { getLocales } from '../../../../../../components/Table/newTable';

export const _empty = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => ({ key: i, isEmpty: true }));

const getAdmLevel2 = ({
  isOwnData,
  options,
  project,
  language,
  user,
}) => {
  if (!isOwnData && options && options.administrativeLevel2Labels) {
    if (project) {
      if (options.administrativeLevel2Labels[project.country]) {
        return options.administrativeLevel2Labels[project.country][language] || 'Territory';
      }
    }
  }

  if (user && options && options.administrativeLevel2Labels && options.administrativeLevel2Labels[user.country] && options.administrativeLevel2Labels[user.country][language]) {
    return options.administrativeLevel2Labels[user.country][language] || 'Territory';
  }

  return 'Territory';
}

const TagStyle = styled.div`
  width: 91px;
  background: #F0F9FF;
  border-radius: 16px;
  display: flex;
  padding: 2px 10px;

  .flex-c {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .bullet {
    width: 6px;
    height: 6px;
    margin-right: 5px;
    background: #0BA5EC;
    border-radius: 100%;
  }

  .label p {
    margin-bottom: 0px;
    font-size: 12px;
    color: #026AA2;
  }

  &.proposed {
    background: #F9F0FF;

    .bullet {
      background: #9B5FEE;
    }

    .label p {
      color: #722ED1;
    }
  }

  &.completed {
    background: #ECFDF3;

    .bullet {
      background: #12B76A;
    }

    .label p {
      color: #039855;
    }
  }
`;

const getTag = (all, t = (s) => s) => {
  if (typeof all === 'string') {
    if (all === 'completed') {
      return (
        <TagStyle className="tag completed">
          <div className="flex-c jus-c">
            <div className="bullet" />
          </div>
          <div className="label">
            <p>{t('Completed')}</p>
          </div>
        </TagStyle>
      );
    }

    if (all === 'inProgress') {
      return (
        <TagStyle className="tag in-progress">
          <div className="flex-c jus-c">
            <div className="bullet" />
          </div>
          <div className="label">
            <p>{t('In Progress')}</p>
          </div>
        </TagStyle>
      );
    }

    return (
      <TagStyle className="tag proposed">
        <div className="flex-c jus-c">
          <div className="bullet" />
        </div>
        <div className="label">
          <p>{t('Proposed')}</p>
        </div>
      </TagStyle>
    );
  }

  if (all) {
    if (typeof all.completedAt === 'string') {
      return <Tag style={{ width: 80, textAlign: 'center', marginLeft: 0 }} color="green">{t('Completed')}</Tag>;
    }
    if (Array.isArray(all.events) && all.events.length) {
      return <Tag style={{ width: 80, textAlign: 'center' }} color="purple">{t('In Progress')}</Tag>;
    }
  }
  return <Tag
    style={{
      color: '#722ED1', border: '1px solid #D3ADF7', background: '#F9F0FF', width: 80, textAlign: 'center',
    }}
  >
    {t('Proposed')}
  </Tag>;
}

const Style = styled.div`
  min-height: 550px;
  overflow-x: auto;


  .ant-table-column-sort {
    background: unset;
  }

  .cursor-pointer {
    cursor: pointer;
    user-select: none;
  }

  .ant-table-body {
    min-height: 328px;
  }
`;

const getOpt = ({ key = '', val = '', options = {} }) => {
  if (options[key]) {
    const v = options[key].find((o) => o.value === val);
    if (v) {
      return v.label;
    }
  }

  return val;
}

const RecTable = ({
  page,
  total,
  dataSource,
  PmeDashboardGetWithFilters,
  setPage,
  initialFetchIsDone,
  t,
  isOwnData = false,
  isOverview = false,
  filters = {},
  organisationName,
  setLoading = () => { },
  options = {},
  project,
  language,
  user,
  maxHeight = 470,
  minHeight = 550,
}) => {
  const [activeRec, setActiveRec] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [toggledKeys, setToggledKeys] = useState([]);
  const loadingKeys = useRef({});

  const { mod } = useModule();

  const columns = useMemo(() => {
    const columnsToReturn = [
      {
        title: t('Location'),
        dataIndex: 'area',
        className: 'show',
        key: 'area',
        ellipsis: true,
        render: (area, all) => (
          <Tooltip title={area}>
            <a
              href={
                all.locationType === 'Mine Site'
                  ? `/app/${mod}/analysis/scl/${all.locationId}` :
                  `/app/${mod}/view/location/${all.locationId}`} target="_blank"
              rel="noreferrer"
              className="ellipsis-text"
            >
              {area}
            </a>
          </Tooltip>
        )
      },
      {
        title: getAdmLevel2({
          isOwnData,
          options,
          project,
          language,
          user,
        }),
        dataIndex: 'admLevel2',
        key: 'admLevel2',
        ellipsis: true,
        className: 'show',
        width: '10%',
        render: (admLevel2, all) => {
          const _val = all.kids
            ? (all.kids[0].admLevel2 ? all.kids[0].admLevel2 : '')
            : (all.admLevel2 ? all.admLevel2 : '');
          return <Tooltip title={_val}>{_val}</Tooltip>;
        },
      },
      {
        title: t('Dashboard::Risk Type'),
        dataIndex: 'riskType',
        key: 'riskType',
        width: '11%',
        ellipsis: true,
        className: 'show',
        render: (riskType, all) => {
          if (Array.isArray(riskType)) {
            if (riskType.length === 1) {
              const _val = getOpt({ key: 'riskCategory', val: riskType[0], options });
              return <Tooltip title={_val}>{_val}</Tooltip>;
            }
            return (
              <div className="more-tag">
                <div className="label flex-c jus-c">
                  <Tooltip title={t('Types')}>
                    {t('Types')}
                  </Tooltip>
                </div>
                <div className="value">
                  {riskType.length}
                </div>
              </div>
            );
          }
          const _val = getOpt({ key: 'riskCategory', val: riskType, options });
          return <Tooltip title={_val}>{_val}</Tooltip>;
        },
      },
      {
        title: t('Measure Type'),
        dataIndex: 'measure',
        className: 'show',
        key: 'measure',
        ellipsis: true,
        render: (typeOfMeasure, all) => {
          if (Array.isArray(typeOfMeasure)) {
            if (typeOfMeasure.length === 1) {
              const _val = getOpt({ key: 'riskMeasures', val: typeOfMeasure[0], options });
              return <Tooltip title={_val}>{_val}</Tooltip>;
            }
            return (
              <div className="more-tag">
                <div className="label flex-c jus-c">
                  <Tooltip title={t('Measures')}>
                    {t('Measures')}
                  </Tooltip>
                </div>
                <div className="value">
                  {typeOfMeasure.length}
                </div>
              </div>
            );
          }
          const val = getOpt({ key: 'riskMeasures', val: typeOfMeasure, options });
          const _val = val ? val : '';
          return <Tooltip title={_val}>{_val}</Tooltip>;
        },
        // width: '17%',
      },
      {
        title: t('Responsibility'),
        dataIndex: 'responsibility',
        key: 'responsibility',
        ellipsis: true,
        className: 'show',
        render: (responsibility, all) => {
          if (Array.isArray(responsibility)) {
            if (responsibility.length === 0) {
              return '';
            }
            if (responsibility.length === 1) {
              return responsibility[0];
            }
            return (
              <div className="more-tag">
                <div className="label flex-c jus-c">
                  <Tooltip title={t('Stakeholders')}>
                    {t('Stakeholders')}
                  </Tooltip>
                </div>
                <div className="value">
                  {responsibility.length}
                </div>
              </div>
            )
          }

          const _val = responsibility ? responsibility : '';
          return <Tooltip title={_val}>{_val}</Tooltip>;
        },
        // width: '17%',
      },
      {
        title: t('Proponent'),
        dataIndex: 'stakeholder',
        className: 'show',
        key: 'stakeholder',
        ellipsis: true,
        render: (stakeholder, all) => {
          if (Array.isArray(stakeholder)) {
            if (stakeholder.length === 1) {
              return stakeholder[0];
            }
            return (
              <div className="more-tag">
                <div className="label flex-c jus-c">
                  <Tooltip title="Proponents">
                    {t('Proponents')}
                  </Tooltip>
                </div>
                <div className="value">
                  {stakeholder.length}
                </div>
              </div>
            )
          }
          const _val = all ? all.source || '' : '';
          return <Tooltip title={_val}>{_val}</Tooltip>;
        },
        // width: '17%',
      },
      {
        title: t('Status'),
        dataIndex: 'status',
        className: 'show',
        key: 'status',
        width: 139,
        render: (status) => {
          return status ? (
            <div style={{ flexDirection: 'row', justifyContent: 'center', width: '100%' }}>
              {getTag(status, t)}
            </div>
          ) : ''
        }
      },
      {
        title: <Tooltip title={t('Submission Date')}>{t('Submission Date')} </Tooltip>,
        dataIndex: 'createdAt',
        className: 'show',
        key: 'createdAt',
        render: (date) => {
          const _val = date ? moment(date).format('DD MMM YYYY') : '';
          return <Tooltip title={_val}>{_val}</Tooltip>;
        },
        width: 130,
        ellipsis: true,
      },
      {
        title: '',
        className: 'show',
        render: (r, all) => !all.kids ? all.id ? (
          <ActionsColumnStyle
            className="recs-eye"
            onClick={() => setActiveRec(all)}
          >
            <div>
              <EyeOutlined />
            </div>
          </ActionsColumnStyle>
        ) : null : null,
        width: 60,
      },
    ];

    if (!isOverview) {
      return columnsToReturn.filter(c => c.key !== 'stakeholder');
    }

    return columnsToReturn;
  }, [t, isOverview, options]);

  const informationSource = useMemo(() => {
    if (isOwnData) {
      return t('Me');
    }

    if (isOverview) {
      if (activeRec && activeRec.source) {
        return activeRec.source;
      }
    }

    if (organisationName) {
      return organisationName;
    }

    return '';
  }, [organisationName, isOwnData, isOverview, activeRec, t]);

  const data = useMemo(() => {
    const locationNames = {};

    dataSource.forEach((d) => {
      if (locationNames[d.area]) {
        locationNames[d.area].push(d);
      } else {
        locationNames[d.area] = [d];
      }
    });

    return Object.keys(locationNames).reduce((all, key) => {
      const loc = locationNames[key];
      const first = loc[0];
      const locationId = first.datastakeId;

      if (loc.length > 1) {
        const riskType = [];
        const measure = [];
        const source = [];
        const statuses = [];
        const responsibility = [];

        loc.forEach((l) => {
          const rT = l.riskType;

          if (!riskType.includes(rT) && rT && rT.length) {
            riskType.push(rT);
          }

          if (l.responsibility && !responsibility.includes(l.responsibility)) {
            responsibility.push(l.responsibility);
          }

          if (!statuses.includes(l.status) && l.status.length) {
            statuses.push(l.status);
          }

          const m = l.measure;

          if (!measure.includes(m) && m && m.length) {
            measure.push(m);
          }

          if (l.source) {
            if (!source.includes(l.source)) {
              source.push(l.source);
            }
          }

        });
        const status = statuses.includes('inProgress') ? 'inProgress' : (statuses.includes('proposed') && statuses.includes('completed')) ? 'inProgress' : (statuses.includes('completed') && !statuses.includes('proposed')) ? 'completed' : 'proposed';
        all.push({
          key: `${first.area}-${page}`,
          riskType,
          measure: measure,
          status,
          responsibility,
          stakeholder: source,
          area: first.area,
          kids: loc.map((l, i) => ({ ...l, isLast: i === loc.length - 1, isChild: true, locationId })),
          locationId,
          locationType: first.locationType,
        });
      } else {
        all.push({
          ...first,
          locationId,
          key: `${first.area}-${page}`,
        });
      }
      return all;
    }, []);
  }, [dataSource, page]);

  const pages = useMemo(() => Math.ceil(total / 8), [total]);

  const canGoNext = useMemo(() => {
    if (total === 0) {
      return false;
    }

    if (page !== pages) {
      return true;
    }

    return false;
  }, [page, total, pages]);

  const canGoPrev = useMemo(() => page !== 1, [page]);

  const goNext = () => {
    const newPage = page + 1;
    setPage(newPage);
    PmeDashboardGetWithFilters({
      setLoading,
      widget: 'recommendations',
      noReset: true,
      filters,
      pagination: {
        page: newPage,
        perPage: 8,
      },
    }, (success) => {
      if (success) {
        setPage(newPage);
      }
    });
  };

  const goPrev = () => {
    const newPage = page - 1;
    setPage(newPage);
    PmeDashboardGetWithFilters({
      setLoading,
      noReset: true,
      widget: 'recommendations',
      filters,
      pagination: {
        page: newPage,
        perPage: 8,
      },
    }, (success) => {
      if (success) {
        setPage(newPage);
      }
    });
  };

  return (
    <Style className="flex-1" style={{ minHeight }}>
      {data.length ? (
        <div className='daf-table-wrapper'>
          <Table
            locale={getLocales(t)}
            size="small"
            columns={columns}
            sticky
            scroll={{ y: maxHeight }}
            style={{ minHeight: dataSource.length > 0 ? maxHeight : 0 }}
            expandable={{
              onExpandedRowsChange: (r) => {
                setExpandedRows(r);
              },
              expandedRowKeys: expandedRows,
              rowExpandable: (r) => (r?.kids || []).length,
              expandedRowRender: (r) => {
                return (
                  <Animated isActive={toggledKeys.includes(r.key)}>
                    <Table
                      locale={getLocales(t)}
                      className="no-header-table"
                      columns={columns}
                      dataSource={r.kids}
                      expandable
                      expandedRowRender={() => null}
                      doInject={false}
                      expandIcon={() => <button type="button" className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" aria-label="Expand row"></button>}
                      pagination={false}
                    />
                  </Animated>
                )
              }
            }}
            rowClassName={(rec) => expandedRows.includes(rec.key) ? 'expanded' : ''}
            pagination={{
              current: page,
              total: total,
              pageSize: 8,
              onChange: (e) => PmeDashboardGetWithFilters({
                setLoading,
                widget: 'recommendations',
                noReset: true,
                filters,
                pagination: {
                  page: e,
                  perPage: 8,
                },
              }, (success) => {
                if (success) {
                  setPage(e);
                }
              }),
              showSizeChanger: false,
            }}
            dataSource={data}
            expandIcon={(props) => expandIcon({ ...props, arrows: true, toggledKeys, setToggledKeys, loadingKeys })}
          />
          <TablePagination
            t={t}
            page={page}
            totalPages={pages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={total}
            doTotalItems
            perPage={8}
          />
        </div>
      ) : null}
      {(dataSource.length === 0 && initialFetchIsDone) && (
        <div className='daf-table-wrapper'>
          <Table
            locale={getLocales(t)}
            size="small"
            columns={columns}
            sticky
            scroll={{ y: maxHeight }}
            style={{ minHeight: maxHeight }}
            expandable={{
              onExpandedRowsChange: (r) => {
                setExpandedRows(r);
              },
              expandedRowKeys: expandedRows,
              rowExpandable: (r) => (r?.kids || []).length,
              expandedRowRender: (r) => {
                return (
                  <Animated isActive={toggledKeys.includes(r.key)}>
                    <Table
                      locale={getLocales(t)}
                      className="no-header-table"
                      columns={columns}
                      dataSource={r.kids}
                      expandable
                      expandedRowRender={() => null}
                      doInject={false}
                      expandIcon={() => <button type="button" className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" aria-label="Expand row"></button>}
                    />
                  </Animated>
                )
              }
            }}
            rowClassName={(rec) => expandedRows.includes(rec.key) ? 'expanded' : ''}
            pagination={{
              current: page,
              total: total,
              pageSize: 8,
              onChange: (e) => PmeDashboardGetWithFilters({
                setLoading,
                noReset: true,
                widget: 'recommendations',
                filters,
                pagination: {
                  page: e,
                  perPage: 8,
                },
              }, (success) => {
                if (success) {
                  setPage(e);
                }
              }),
              showSizeChanger: false,
            }}
            dataSource={_empty}
            expandIcon={(props) => expandIcon({ ...props, arrows: true, toggledKeys, setToggledKeys, loadingKeys })}
          />
          <TablePagination
            t={t}
            page={page}
            totalPages={pages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={total}
            doTotalItems
            perPage={8}
          />
        </div>
      )}
      <Modal
        open={!!activeRec}
        title={(
          <ModalHeader title={activeRec && activeRec.title ? activeRec.title : '-'} />
        )}
        destroyOnClose
        footer={[
          <>
            {activeRec && (
              <a
                target="_blank"
                href={
                  activeRec.locationType === 'Mine Site'
                    ? `/app/${mod}/view/scl/${activeRec.datastakeId}/risk/${activeRec.riskCategoryCamelCase}/${activeRec.riskTypeCamelCase}?sourceId=${activeRec.sourceId}` :
                    `/app/${mod}/view/location/${activeRec.datastakeId}/risk/${activeRec.riskCategoryCamelCase}/${activeRec.riskTypeCamelCase}?sourceId=${activeRec.sourceId}`
                }

                rel="noreferrer"
              >
                <Button type="primary">
                  {t('View Risk')}
                </Button>
              </a>
            )}
          </>
        ]}
        centered
        width={888}
        onCancel={() => setActiveRec(null)}
      >
        {activeRec && (
          <ModalContent
            t={t}
            layout="LAYOUT_B"
            rec={{ ...activeRec, completionStatus: getType(activeRec) }}
            informationSource={informationSource}
            actions={{ renderStatus: ({ t, status }) => getTag(status, t) }}
          />
        )}
      </Modal>
    </Style>
  );
}

export default RecTable;
